import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { paypalCaptureOrder } from '../services/firebase';

/**
 * Landing page after PayPal approval redirect.
 * URL: /payment-return?token=PAYPAL_ORDER_ID
 *
 * Flow:
 *   1. Read token (PayPal order ID) from URL params
 *   2. Read { orderId, orderNumber } from sessionStorage (set in CheckoutPage)
 *   3. Call paypalCaptureOrder Cloud Function
 *   4. Navigate to /confirmed/:orderNumber on success
 */
const PayPalReturnPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const run = async () => {
      const paypalOrderId = searchParams.get('token');

      // PayPal cancelled — no token
      if (!paypalOrderId) {
        navigate('/checkout', { state: { paypalCancelled: true }, replace: true });
        return;
      }

      let orderId = '';
      let orderNumber = '';
      try {
        const raw = sessionStorage.getItem('paypal_pending');
        if (raw) {
          const parsed = JSON.parse(raw);
          orderId = parsed.orderId || '';
          orderNumber = parsed.orderNumber || '';
        }
      } catch { /* ignore */ }

      try {
        const result = await paypalCaptureOrder({ paypalOrderId }) as any;
        const d = result.data as any;
        if (d?.success) {
          try { sessionStorage.removeItem('paypal_pending'); } catch { /* ignore */ }
          setStatus('success');
          navigate(`/confirmed/${orderNumber}`, {
            state: { orderId, paypalPaid: true },
            replace: true,
          });
        } else {
          throw new Error(d?.error || 'Capture failed');
        }
      } catch (err: any) {
        console.error('[PAYPAL RETURN] Capture error:', err);
        setStatus('error');
        setErrorMsg(err?.message || 'Payment could not be confirmed.');
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === 'error') {
    return (
      <div style={{
        minHeight: '100vh', background: '#111322', display: 'flex',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 16, padding: 24,
      }}>
        <div style={{ fontSize: 48 }}>❌</div>
        <p style={{ color: '#f87171', fontWeight: 700, fontSize: '1.1rem', textAlign: 'center' }}>
          Payment could not be confirmed
        </p>
        <p style={{ color: '#9ca3af', fontSize: '0.85rem', textAlign: 'center' }}>{errorMsg}</p>
        <p style={{ color: '#9ca3af', fontSize: '0.8rem', textAlign: 'center', maxWidth: 320 }}>
          If your PayPal account was charged, please contact us — your order may still have been placed.
        </p>
        <button
          onClick={() => navigate('/', { replace: true })}
          style={{
            marginTop: 8, padding: '12px 28px', background: '#E8B14A',
            color: '#000', border: 'none', borderRadius: 8,
            fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem',
          }}
        >
          Back to menu
        </button>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', background: '#111322', display: 'flex',
      flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16,
    }}>
      <div style={{
        width: 52, height: 52, border: '4px solid #E8B14A', borderTopColor: 'transparent',
        borderRadius: '50%', animation: 'spin 0.8s linear infinite',
      }} />
      <p style={{ color: '#E8B14A', fontWeight: 700, fontSize: '1.05rem' }}>
        Confirming your payment…
      </p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default PayPalReturnPage;
